<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-subtitle-1 bg-blue-grey-lighten-4 mb-0">
              Search Form
            </v-card-title>

            <v-card-text>
              <v-form @submit.prevent="submit">
                <v-row class="pt-4 pb-0">
                  <v-col md="3" cols="12">
                    <v-date-input input-format="dd/mm/yyyy" label="From Date" density="compact" prepend-icon=""
                      prepend-inner-icon="mdi mdi-calendar-month" single-line variant="outlined"
                      bg-color="grey-lighten-4" v-model="from_date.value.value"
                      :error-messages="from_date.errorMessage.value" autocomplete="off" clearable></v-date-input>
                  </v-col>

                  <v-col md="3" cols="12">
                    <v-date-input input-format="dd/mm/yyyy" label="To Date" density="compact" single-line
                      prepend-icon="" prepend-inner-icon="mdi mdi-calendar-month" variant="outlined"
                      bg-color="grey-lighten-4" v-model="to_date.value.value"
                      :error-messages="to_date.errorMessage.value" autocomplete="off" clearable></v-date-input>
                  </v-col>

                  <v-col lg="2" md="2" cols="6">
                    <v-btn class="bg-brand" type="submit" :loading="loading">
                      Search
                    </v-btn>
                  </v-col>

                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="text-subtitle-1 bg-blue-grey-lighten-4 mb-0">
              Visit Record
            </v-card-title>

            <v-card-text>
              <v-row class="py-2">
                <v-col md="4" cols="12">
                  <v-text-field v-model="search" append-inner-icon="mdi-magnify" label="Search" single-line hide-details
                    density="compact" variant="outlined" bg-color="grey-lighten-4" class="pl-2"
                    clearable></v-text-field>
                </v-col>

                <v-col md="5" cols="12">
                  <div class="d-flex align-center flex-column">
                    <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                      <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">
                        CSV
                      </v-btn>

                      <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">
                        EXCEL
                      </v-btn>

                      <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">
                        PDF
                      </v-btn>
                    </v-btn-group>
                  </div>
                </v-col>
              </v-row>

              <v-data-table :headers="headers" :items="getSchema" :loading="loading"
                v-model:items-per-page="itemsPerPage" :search="search" :hover="true" density="compact">
                <template v-slot:item.index="{ index }">
                  {{ index + 1 }}
                </template>

                <template v-slot:item.image="{ item }">
                  <div class="d-flex justify-center">
                    <v-img :src="getImageUrl(item?.image)" cover class="preview-image"
                      @click="openImageDialog(item?.image)"></v-img>
                  </div>
                </template>

                <template v-slot:item.image_out="{ item }">
                  <div class="d-flex justify-center">
                    <v-img :src="getImageUrl(item?.image_out)" cover class="preview-image"
                      @click="openImageDialog(item?.image_out)"></v-img>
                  </div>
                </template>

                <template v-slot:item.check_in_remarks="{ value }">
                  <div style="width: 200px; white-space: normal;">
                    {{ value }}
                  </div>
                </template>

                <template v-slot:item.check_out_remarks="{ value }">
                  <div style="width: 200px; white-space: normal;">
                    {{ value }}
                  </div>
                </template>

                <template v-slot:item.approval_status="{ item }">
                  <v-chip :color="getApprovalColor(item)" size="small" class="text-capitalize">
                    {{ getApprovalStatus(item) }}
                  </v-chip>
                </template>

              </v-data-table>
            </v-card-text>
          </v-card>

          <v-dialog v-model="imageDialog" max-width="900" persistent>
            <v-card>
              <v-card-title class="d-flex justify-space-between align-center">
                Image Preview
                <v-btn icon="mdi-close" variant="text" @click="imageDialog = false"></v-btn>
              </v-card-title>

              <v-card-text class="text-center pa-4">
                <v-img :src="selectedImage" max-height="70vh" contain></v-img>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import noPreview from "@/assets/no_preview.png";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";
import leadStatus from "@/utils/leadStatus";
import { fileServerUrl } from "@/store/api";

export default {
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    search: "",
    dialog: false,
    imageDialog: false,
    selectedImage: "",
    data: {},
    previewWatermark: noPreview,
    leadStatus,
    headers: [
      { title: "#SL", align: "center", key: "index", width: "7%", sortable: false },
      { title: "Employee Name", key: "employee.name", align: "left", sortable: false },
      { title: "Meeting date", key: "meeting_date", align: "left", sortable: false },
      { title: "Organization Name", key: "company_name", align: "left", sortable: false },
      { title: "Check in address", key: "check_in_address", align: "start", sortable: false },
      { title: "Check out address", key: "check_out_address", align: "start", sortable: true },
      { title: "Check in date & time", key: "check_in_time", align: "center", sortable: false },
      { title: "Check out date & time", key: "check_out_time", align: "center", sortable: false },
      { title: "Check in Image", key: "image", align: "center", sortable: false, width: '100px' },
      { title: "Check out Image", key: "image_out", align: "center", sortable: false, width: '100px' },
      { title: "Check In Remarks", key: "check_in_remarks", align: "start", sortable: false },
      { title: "Check Out Remarks", key: "check_out_remarks", align: "start", sortable: false },
      { title: "Status", key: "approval_status", align: "center", sortable: false },
    ],
  }),
  computed: {
    getSchema() {
      return this.$store.getters.getHrmEmployeeMeetingList || [];
    },
  },
  setup() {
    const { handleSubmit } = useForm({
      validationSchema: yup.object({}),
    });
    const from_date = useField("from_date");
    const to_date = useField("to_date");
    const loading = ref(false);
    const store = useStore();

    const submit = handleSubmit(async () => {
      const from = from_date.value.value;
      const to = to_date.value.value;
      if (from && to && new Date(from) > new Date(to)) {
        toast.warning("From Date cannot be after To Date.");
        return;
      }
      loading.value = true;
      try {
        await store.dispatch("getHrmEmployeeMeetingList", {
          from_date: from ? formattedDate(from) : null,
          to_date: to ? formattedDate(to) : null,
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || "Unable to load visit records.");
      } finally {
        loading.value = false;
      }
    });

    return { submit, from_date, to_date, loading };
  },

  methods: {
    getApprovalStatus(item) {
      return item?.claim_item?.claim_master?.approval_status || "Not Applied";
    },
    getApprovalColor(item) {
      const status = this.getApprovalStatus(item).toLowerCase();
      if (status === "approved") return "green";
      if (status === "pending") return "orange";
      if (status === "rejected" || status === "cancelled") return "red";
      return "blue";
    },
    getExportData() {
      const columns = [
        "#SL", "Employee Name", "Meeting Date", "Organization Name",
        "Check In Address", "Check Out Address", "Check In Date & Time",
        "Check Out Date & Time", "Check In Remarks", "Check Out Remarks", "Status",
      ];
      const rows = this.getSchema.map((item, index) => [
        index + 1,
        item.employee?.name || item.employee?.full_name || "",
        item.meeting_date || "",
        item.company_name || "",
        item.check_in_address || "",
        item.check_out_address || "",
        item.check_in_time || "",
        item.check_out_time || "",
        item.check_in_remarks || "",
        item.check_out_remarks || "",
        this.getApprovalStatus(item),
      ]);
      return { columns, rows };
    },
    exportToPDF() {
      const { columns, rows } = this.getExportData();
      this.$exportTableToPDF(columns, rows, "Visit Record", "visit_records_" + this.$fileDate());
    },
    exportToExcel() {
      const { columns, rows } = this.getExportData();
      this.$exportTableToExcel([columns, ...rows], "visit_records_" + this.$fileDate());
    },
    exportToCSV() {
      const { columns, rows } = this.getExportData();
      this.$exportTableToCSV([columns, ...rows], "visit_records_" + this.$fileDate());
    },
    getImageUrl(image) {
      if (!image) return this.previewWatermark;
      const path = String(image).trim();
      if (/^(https?:\/\/|data:|blob:)/i.test(path)) return path;
      return `${String(fileServerUrl).replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
    },
    openImageDialog(image) {
      if (!image) return;
      this.selectedImage = this.getImageUrl(image);
      this.imageDialog = true;
    },
  },

  mounted() {
    this.from_date.value.value = new Date();
    this.to_date.value.value = new Date();
    this.$nextTick(() => this.submit());
  },
};
</script>

<style scoped>
.v-card-title {
  height: 40px;
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
}

.preview-image {
  width: 72px;
  height: 52px;
  cursor: pointer;
}
</style>
